/** „Sprzedaż PH” v2 — sending the sheet to the reps (`repSalesWeek.*`), merged in `i18n/extra.ts`. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const repSalesWeekPl = {
  send: {
    button: 'Wyślij do handlowców',
    title: 'Wyślij arkusz Sprzedaż PH',
    recipients: 'Odbiorcy',
    team: 'Cały mój zespół ({count})',
    selected: 'Wybrani…',
    selectAll: 'Zaznacz wszystkich',
    selectNone: 'Odznacz',
    pickHint: 'Zaznacz co najmniej jednego handlowca.',
    noOwners: 'Brak handlowców w Twoim zakresie.',
    note: 'Notatka (opcjonalnie)',
    notePlaceholder: 'Np. proszę o komentarze do spadków i niskiej marży.',
    noteCounter: '{count}/{max}',
    due: 'Termin komentarzy',
    dueHint: 'Przypomnienie w aplikacji dzień przed terminem i w dniu terminu.',
    noDue: 'Bez terminu',
    channel: 'Kanał',
    channelApp: 'Aplikacja (push + powiadomienie)',
    channelEmail: '+ e-mail z Excelem',
    cancel: 'Anuluj',
    submit: 'Wyślij do {count} handlowców',
    submitTeam: 'Wyślij do zespołu',
    sent: 'Wysłano do {sent} z {recipients} handlowców',
    sentPartial: 'Wysłano do {sent} z {recipients} handlowców — nie udało się: {failed}',
    error: 'Nie udało się wysłać arkusza',
  },
  status: {
    title: 'Status tygodnia {week}',
    sentInfo: 'wysłano {when}',
    dueInfo: 'termin komentarzy {when}',
    notSentInfo: 'w tym tygodniu nie było wysyłki',
    week: 'Tydzień',
    weekCurrent: 'Bieżący tydzień ({week})',
    weekPast: 'Tydzień {week} ({from})',
    done: 'Gotowe ✓',
    doneAt: 'wysłał(a) kierownikowi {when}',
    unopened: 'Nie otworzył',
    comments: '{done}/{need} komentarzy',
    openedAt: 'otworzył(a) {when}',
    notSent: 'Nie wysłano',
    remind: 'Przypomnij',
    remindAll: 'Przypomnij wszystkim z brakami ({count})',
    reminded: 'Wysłano przypomnienia: {count}',
    remindError: 'Nie udało się wysłać przypomnienia',
    empty: 'Brak handlowców w zakresie.',
    error: 'Nie udało się wczytać statusu tygodnia.',
  },
  history: {
    title: 'Historia wysyłek',
    when: 'Kiedy',
    who: 'Kto',
    to: 'Do kogo',
    channel: 'Kanał',
    note: 'Notatka',
    due: 'Termin',
    auto: 'automatycznie',
    reps: '{count} handlowców',
    app: 'aplikacja',
    appEmail: 'aplikacja + e-mail',
    empty: 'Jeszcze nie było wysyłek.',
  },
  since: {
    up: '▲ +{value} od {day}',
    down: '▼ {value} od {day}',
    trendChanged: 'kolor: {before} → {now}',
    commentAdded: 'komentarz od {day}',
    days: { 1: 'pn', 2: 'wt', 3: 'śr', 4: 'czw', 5: 'pt', 6: 'sob', 7: 'nd' },
    daysLong: { 1: 'poniedziałku', 2: 'wtorku', 3: 'środy', 4: 'czwartku', 5: 'piątku', 6: 'soboty', 7: 'niedzieli' },
  },
  hero: {
    due: 'termin: {when}',
    noDue: 'bez terminu',
    doneButton: 'Gotowe — wyślij kierownikowi',
    doneConfirm: 'Wysłać kierownikowi informację, że arkusz jest gotowy?',
    doneSent: 'Wysłano kierownikowi o {time}',
    doneOk: 'Kierownik dostał powiadomienie',
    doneError: 'Nie udało się wysłać',
    missingBeforeDone: 'Bez komentarza: {count}. Wysłać mimo to?',
    week: 'tydzień {week}',
  },
  cards: {
    unopened: 'nie otworzył',
    done: 'gotowe ✓',
  },
  settings: {
    section: 'Sprzedaż PH',
    sectionHint: 'Cotygodniowa wysyłka arkusza handlowca na aplikację (push + powiadomienie) i termin komentarzy.',
    readOnly: 'Podgląd — zmieniać może tylko administrator.',
    auto: 'Automatyczna wysyłka co tydzień',
    autoTitle: 'Wysyłka automatyczna',
    day: 'Dzień tygodnia',
    time: 'Godzina',
    channel: 'Kanał',
    managersCopy: 'Kopia arkuszy zespołu dla kierowników',
    dueTitle: 'Termin komentarzy',
    dueDays: 'Dni od wysyłki',
    dueHour: 'Godzina terminu',
    dueExample: 'Wysyłka: {day}, {time} → termin komentarzy: {dueDay}, {hour}:00.',
    save: 'Zapisz',
    saved: 'Ustawienia zapisane',
    saveFailed: 'Nie udało się zapisać ustawień',
    loadFailed: 'Nie udało się wczytać ustawień Sprzedaży PH.',
  },
};

export const repSalesWeekEn: Shape<typeof repSalesWeekPl> = {
  send: {
    button: 'Send to reps',
    title: 'Send the rep sales sheet',
    recipients: 'Recipients',
    team: 'My whole team ({count})',
    selected: 'Selected…',
    selectAll: 'Select all',
    selectNone: 'Clear',
    pickHint: 'Select at least one rep.',
    noOwners: 'No reps in your scope.',
    note: 'Note (optional)',
    notePlaceholder: 'E.g. please comment on drops and low margins.',
    noteCounter: '{count}/{max}',
    due: 'Comment deadline',
    dueHint: 'In-app reminder the day before and on the deadline day.',
    noDue: 'No deadline',
    channel: 'Channel',
    channelApp: 'App (push + notification)',
    channelEmail: '+ e-mail with Excel',
    cancel: 'Cancel',
    submit: 'Send to {count} reps',
    submitTeam: 'Send to the team',
    sent: 'Sent to {sent} of {recipients} reps',
    sentPartial: 'Sent to {sent} of {recipients} reps — failed: {failed}',
    error: 'Could not send the sheet',
  },
  status: {
    title: 'Week {week} status',
    sentInfo: 'sent {when}',
    dueInfo: 'comment deadline {when}',
    notSentInfo: 'nothing was sent this week',
    week: 'Week',
    weekCurrent: 'Current week ({week})',
    weekPast: 'Week {week} ({from})',
    done: 'Done ✓',
    doneAt: 'sent to the manager {when}',
    unopened: 'Not opened',
    comments: '{done}/{need} comments',
    openedAt: 'opened {when}',
    notSent: 'Not sent',
    remind: 'Remind',
    remindAll: 'Remind everyone with gaps ({count})',
    reminded: 'Reminders sent: {count}',
    remindError: 'Could not send the reminder',
    empty: 'No reps in scope.',
    error: 'Could not load the week status.',
  },
  history: {
    title: 'Sending history',
    when: 'When',
    who: 'Who',
    to: 'To',
    channel: 'Channel',
    note: 'Note',
    due: 'Deadline',
    auto: 'automatic',
    reps: '{count} reps',
    app: 'app',
    appEmail: 'app + e-mail',
    empty: 'Nothing has been sent yet.',
  },
  since: {
    up: '▲ +{value} since {day}',
    down: '▼ {value} since {day}',
    trendChanged: 'colour: {before} → {now}',
    commentAdded: 'comment since {day}',
    days: { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' },
    daysLong: { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' },
  },
  hero: {
    due: 'due {when}',
    noDue: 'no deadline',
    doneButton: 'Done — send to manager',
    doneConfirm: 'Let your manager know the sheet is ready?',
    doneSent: 'Sent to the manager at {time}',
    doneOk: 'Your manager has been notified',
    doneError: 'Could not send',
    missingBeforeDone: 'Without a comment: {count}. Send anyway?',
    week: 'week {week}',
  },
  cards: {
    unopened: 'not opened',
    done: 'done ✓',
  },
  settings: {
    section: 'Rep sales sheet',
    sectionHint: 'Weekly sending of the rep sheet to the app (push + notification) and the comment deadline.',
    readOnly: 'Read-only — only an administrator can change it.',
    auto: 'Send automatically every week',
    autoTitle: 'Automatic sending',
    day: 'Weekday',
    time: 'Time',
    channel: 'Channel',
    managersCopy: 'Copy of the team sheets for managers',
    dueTitle: 'Comment deadline',
    dueDays: 'Days after sending',
    dueHour: 'Deadline hour',
    dueExample: 'Sending: {day}, {time} → comment deadline: {dueDay}, {hour}:00.',
    save: 'Save',
    saved: 'Settings saved',
    saveFailed: 'Could not save the settings',
    loadFailed: 'Could not load the rep sales settings.',
  },
};
